<template>
  <div class="container py-4">
    <div class="page-card">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="mb-0">Inventory</h4>
        <div class="d-flex gap-2">
          <icon-button
            :func="() => (showBulkAdjust = true)"
            :icon="'tasks'"
            :title="'Bulk adjust'"
            :classes="'akkurate-gray-btn btn-size2'"
            :btnName="'Bulk adjust'"
          />
          <icon-button
            :func="() => (showAdjust = true)"
            :icon="'plus'"
            :title="'Adjust stock'"
            :classes="'akkurate-green-btn btn-size2'"
            :btnName="'Adjust stock'"
          />
        </div>
      </div>
      <b-table
        :items="items"
        :fields="fields"
        :busy="loading"
        responsive
        hover
        small
      >
        <template #cell(available)="data">
          <span :class="{ 'text-danger fw-semibold': data.value <= 5 }">
            {{ data.value }}
          </span>
        </template>
      </b-table>
    </div>

    <b-modal v-model="showAdjust" title="Adjust stock" @ok="submitAdjust">
      <b-form-group label="Product">
        <b-form-select
          class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
          v-model="adjustForm.productId"
          :options="productOptions"
        >
          <template #first>
            <b-form-select-option :value="null" disabled
              >Select product</b-form-select-option
            >
          </template>
        </b-form-select>
      </b-form-group>
      <b-form-group label="Quantity change">
        <b-form-input
          class="form-control"
          v-model.number="adjustForm.quantity"
          type="number"
          placeholder="Use negative to reduce"
        />
        <b-form-text>Positive adds stock; negative removes stock.</b-form-text>
      </b-form-group>
      <b-form-group label="Category">
        <b-form-select
          v-model="adjustForm.category"
          class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
          :options="categoryOptions"
        />
      </b-form-group>
      <b-form-group label="Reason">
        <b-form-input
          class="form-control"
          v-model="adjustForm.reason"
          placeholder="e.g. Stock count, damage"
        />
      </b-form-group>
      <b-form-group label="Notes">
        <b-form-textarea v-model="adjustForm.notes" rows="2" />
      </b-form-group>

      <template #modal-footer="{ ok, cancel }">
        <icon-button
          :func="() => cancel()"
          :icon="'times'"
          :title="'Cancel'"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Cancel'"
        />

        <icon-button
          :func="() => ok()"
          :icon="'check'"
          :title="'Adjust'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Adjust'"
        />
      </template>
    </b-modal>

    <bulk-import-modal
      v-model="showBulkAdjust"
      title="Bulk stock adjustment"
      template-endpoint="import/inventory/template"
      import-endpoint="import/inventory/adjust"
      :store-id="selectedStoreId"
      :show-update-existing="false"
      @imported="load"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import BulkImportModal from "@/views/main/pages/shared/BulkImportModal.vue";
import IconButton from "@/components/IconButton.vue";

export default {
  name: "InventoryPage",
  components: { BulkImportModal, IconButton },
  data() {
    return {
      items: [],
      products: [],
      loading: false,
      showAdjust: false,
      showBulkAdjust: false,
      adjustForm: {
        productId: null,
        quantity: 0,
        category: "normal",
        reason: "",
        notes: "",
      },
      categoryOptions: [
        { value: "normal", text: "Normal adjustment" },
        { value: "damage", text: "Damaged stock" },
        { value: "expired", text: "Expired stock" },
        { value: "theft", text: "Theft / loss" },
        { value: "count", text: "Stock count" },
      ],
      fields: [
        { key: "productSku", label: "SKU" },
        { key: "productName", label: "Product" },
        { key: "quantity", label: "Qty" },
        { key: "reserved", label: "Reserved" },
        { key: "available", label: "Available" },
      ],
    };
  },
  computed: {
    ...mapGetters(["selectedStoreId"]),
    productOptions() {
      return this.products.map((p) => ({
        value: p.id,
        text: `${p.sku} — ${p.name} (${p.stock?.available ?? 0} avail.)`,
      }));
    },
  },
  watch: {
    selectedStoreId() {
      this.load();
      this.loadProducts();
    },
  },
  mounted() {
    this.load();
    this.loadProducts();
  },
  methods: {
    async loadProducts() {
      if (!this.selectedStoreId) return;
      this.products = await ApiService.get("products", {
        storeId: this.selectedStoreId,
      });
    },
    async load() {
      this.loading = true;
      try {
        this.items = await ApiService.get("inventory", {
          storeId: this.selectedStoreId,
        });
      } finally {
        this.loading = false;
      }
    },
    async submitAdjust(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (!this.adjustForm.productId) return;
      try {
        await ApiService.patch("inventory/adjust", {
          productId: this.adjustForm.productId,
          storeId: this.selectedStoreId,
          quantity: this.adjustForm.quantity,
          category: this.adjustForm.category,
          reason: this.adjustForm.reason || null,
          notes: this.adjustForm.notes || null,
        });
        this.showAdjust = false;
        this.adjustForm = {
          productId: null,
          quantity: 0,
          category: "normal",
          reason: "",
          notes: "",
        };
        await this.load();
        await this.loadProducts();
        toast.fire({
          title: "Stock adjusted",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Adjust failed",
          icon: "danger",
        });
      }
    },
  },
};
</script>
